/**
 * Main front end point of entry.
 *
 * Copyright (C) 2018D Noom, Inc.
 * @author hubert
 */

import React from "react";
import { createRoot } from "react-dom/client";

import App from "@/app/App";
import bootstrap from "@/utils/bootstrap";

import "@/api/index";
import { AppProvider } from "./contexts/app-context";

// Patch DOM methods to prevent crashes when Google Translate (or similar
// extensions) reparent text nodes out from under React's virtual DOM.
// See: https://github.com/facebook/react/issues/11538
//
// WHY THIS EXISTS:
// Google Translate wraps text nodes in <font> tags, breaking React's
// assumption that it owns the DOM. When React tries to remove/insert a
// node that Translate has moved, it throws "removeChild: not a child".
//
// OTHER DEFENSES IN PLACE:
// - transWrapTextNodes: "span" in i18n.ts — wraps <Trans> text in <span>
// - eslint-plugin-react-google-translate — catches bare text patterns in
//   our own code at lint time
//
// WHEN TO REMOVE THIS PATCH:
// This patch can be removed once ALL of the following are true:
// 1. @noom/wax-component-library has been updated so its components do
//    not render bare text nodes (wrap internal text in <span> elements)
// 2. Any other Noom-owned dependencies with bare text nodes are updated
// 3. The eslint-plugin-react-google-translate rule is passing clean
// 4. The app has been tested with Google Translate active and no
//    removeChild errors occur without the patch
//
// Until then, this patch is the only safety net for text nodes produced
// by dependencies we don't directly control in this repo.
if (typeof Node !== "undefined") {
  const originalRemoveChild = Node.prototype.removeChild;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Node.prototype as any).removeChild = function <T extends Node>(child: T): T {
    if (child.parentNode !== this) {
      if (child.parentNode) {
        return originalRemoveChild.call(child.parentNode, child) as T;
      }
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const originalInsertBefore = Node.prototype.insertBefore;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (Node.prototype as any).insertBefore = function <T extends Node>(
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      return originalInsertBefore.call(this, newNode, null) as T;
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };
}

bootstrap();

const container = document.getElementById("root");
// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
const root = createRoot(container!);

// Render the app
root.render(
  <AppProvider>
    <App />
  </AppProvider>,
);
